import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useStore } from "@/contexts/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, MapPin, Plus, ShieldCheck, Truck, IndianRupee, CreditCard,
  Timer, Package, Tag, Receipt, StickyNote, CheckCircle2, Sparkles, Home,
  Smartphone,
} from "lucide-react";

const emptyAddr = { label: "Home", name: "", phone: "", line1: "", line2: "", landmark: "", city: "", state: "", pincode: "", is_default: true };

export default function Checkout() {
  const nav = useNavigate();
  const { cart, refresh } = useCart();
  const { etaMinutes, deliveryFee, freeOver } = useStore();

  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState("");
  const [addrDialog, setAddrDialog] = useState(false);
  const [addrForm, setAddrForm] = useState(emptyAddr);
  const [addrBusy, setAddrBusy] = useState(false);
  const [payment, setPayment] = useState("cod"); // cod | razorpay
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);

  const loadAddrs = async () => {
    try {
      const { data } = await api.get("/addresses");
      const items = data.items || [];
      setAddresses(items);
      const def = items.find((a) => a.is_default) || items[0];
      if (def) setSelectedAddr(def.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { refresh(); loadAddrs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAddr = async () => {
    setAddrBusy(true);
    try {
      const { data } = await api.post("/addresses", { ...addrForm });
      toast.success("Address saved");
      setAddrDialog(false);
      setAddrForm(emptyAddr);
      await loadAddrs();
      setSelectedAddr(data.id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAddrBusy(false); }
  };

  // Load Razorpay Checkout.js on demand (once).
  const loadRazorpaySdk = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const place = async (opts = {}) => {
    const upiApp = opts.upiApp || null;                       // 'google_pay' | 'phonepe' | 'paytm' | 'bhim' | null
    // When a UPI quick-launch button is used, we always use the online (Razorpay) rail
    // so the intent flow can trigger. COD flow is unchanged.
    const effPayment = upiApp ? "razorpay" : payment;
    if (!selectedAddr) { toast.error("Pick a delivery address"); return; }
    if (!cart?.items?.length) { toast.error("Cart is empty"); return; }
    setPlacing(true);
    try {
      // 1) Create the internal order (this creates the pending payment row too).
      const { data: created } = await api.post("/orders/checkout", {
        address_id: selectedAddr,
        payment_method: effPayment,
        channel: "web",
        notes: notes?.trim() || undefined,
      });

      // 2) COD → order already reserved, land on success.
      if (effPayment === "cod") {
        toast.success(`Order placed · ${created.order_number}`);
        refresh();
        nav(`/order-success/${created.id}`, { replace: true });
        return;
      }

      // 3) Razorpay → open live checkout modal.
      const ok = await loadRazorpaySdk();
      if (!ok) { toast.error("Could not load Razorpay. Check your connection and retry."); setPlacing(false); return; }

      const { data: rp } = await api.post("/payments/razorpay/order", { order_id: created.id });

      // Optional UPI-intent config: when the user tapped a quick-launch UPI app, we ask
      // Razorpay Checkout to prefer the UPI intent flow on that app. On unsupported
      // devices (desktop/no app installed), Razorpay Checkout automatically falls
      // back to its full UI — zero code paths change for the fallback.
      const upiConfig = upiApp ? {
        config: {
          display: {
            blocks: {
              upi_intent: {
                name: "Pay via UPI",
                instruments: [{ method: "upi", flows: ["intent"], apps: [upiApp] }],
              },
            },
            sequence: ["block.upi_intent"],
            preferences: { show_default_blocks: true },
          },
        },
      } : {};

      const rzp = new window.Razorpay({
        key:         rp.key_id,
        amount:      rp.amount,
        currency:    rp.currency || "INR",
        order_id:    rp.razorpay_order_id,
        name:        "Babu Baazar",
        description: `Order ${created.order_number}`,
        image:       "/brand/babu-mark.png",
        prefill:     { name: created.contact_name || "", email: created.contact_email || "", contact: created.contact_phone || "" },
        theme:       { color: "#dc1f26" },
        notes:       { internal_order_id: created.id, order_number: created.order_number },
        ...upiConfig,
        // Server-side signature verification happens next.
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id:            created.id,
              razorpay_order_id:   resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature:  resp.razorpay_signature,
            });
            toast.success("Payment confirmed");
            refresh();
            nav(`/order-success/${created.id}`, { replace: true });
          } catch (e) {
            toast.error(errMsg(e, "Payment verification failed. Please contact support."));
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            // Cart is intact; user stays on /checkout to retry.
            toast.info("Payment cancelled. Your cart is safe — try again when ready.");
            setPlacing(false);
          },
        },
      });
      rzp.on("payment.failed", (ev) => {
        const desc = ev?.error?.description || "Payment failed. Please retry.";
        toast.error(desc);
        setPlacing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e, "Checkout failed"));
      setPlacing(false);
    }
  };

  const items = cart?.items || [];
  const totals = cart?.totals || {};
  const subtotal = Number(totals.subtotal || 0);
  const tax = Number(totals.tax || 0);
  const discount = Number(totals.discount || 0);
  const packaging = Number(totals.packaging_fee || totals.packaging || 0);
  // Compute a live-preview delivery fee for the customer — final value comes from server.
  const effectiveDelivery = subtotal >= Number(freeOver || 0) ? 0 : Number(deliveryFee || 0);
  const subtotalAfterDisc = Math.max(0, subtotal - discount);
  const total = Number(totals.total_before_delivery || subtotalAfterDisc) + effectiveDelivery + packaging;
  // Product-level savings (MRP − selling price) already computed per-line in cart.
  const mrpSavings = items.reduce(
    (s, it) => s + Math.max(0, (Number(it.mrp || 0) - Number(it.price_snapshot || it.unit_price || it.selling_price || 0))) * (it.qty || 0),
    0,
  );
  const totalSavings = mrpSavings + discount + (Number(freeOver) > 0 && effectiveDelivery === 0 ? Number(deliveryFee || 0) : 0);

  if (!items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center" data-testid="checkout-empty">
        <h1 className="text-3xl font-display font-semibold">Your cart is empty</h1>
        <p className="text-muted-foreground mt-2">Add products before checking out.</p>
        <Button asChild size="lg" className="mt-6 btn-primary-glow"><Link to="/shop">Continue shopping</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8" data-testid="checkout-page">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl md:text-4xl font-display font-semibold">Checkout</h1>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1">
          <ShieldCheck className="w-3 h-3" /> Secure
        </span>
      </div>
      <p className="text-sm text-muted-foreground mt-1">Review your order, pick address, and pay.</p>

      <div className="grid lg:grid-cols-3 gap-5 md:gap-6 mt-6 md:mt-8">
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          {/* Address block */}
          <Card className="border bg-card" data-testid="checkout-address-card">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><MapPin className="w-4 h-4" /></div>
                  <div className="font-display text-base md:text-lg font-semibold">Delivery address</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setAddrDialog(true)} data-testid="add-address-btn"><Plus className="w-3 h-3 mr-1" />Add new</Button>
              </div>

              {addresses.length === 0 && (
                <div className="rounded-xl border border-dashed p-6 text-center text-muted-foreground text-sm">
                  No saved addresses yet. Add one to continue.
                </div>
              )}

              {addresses.length > 0 && (
                <RadioGroup value={selectedAddr} onValueChange={setSelectedAddr} className="space-y-2">
                  {addresses.map((a) => {
                    const active = selectedAddr === a.id;
                    return (
                      <label
                        key={a.id}
                        className={`flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition ${active ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/40 hover:bg-secondary/40"}`}
                        data-testid={`address-option-${a.id}`}
                      >
                        <RadioGroupItem value={a.id} className="mt-1" />
                        <div className="w-9 h-9 rounded-lg bg-secondary text-foreground/70 grid place-items-center shrink-0"><Home className="w-4 h-4" /></div>
                        <div className="flex-1 min-w-0 text-sm leading-snug">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold">{a.name}</div>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-semibold">{a.label || "home"}</span>
                            {a.is_default && <span className="text-[10px] text-primary font-bold uppercase">Default</span>}
                          </div>
                          <div className="text-muted-foreground mt-0.5">{a.phone}</div>
                          <div className="text-muted-foreground">{[a.line1, a.line2, a.landmark].filter(Boolean).join(", ")}</div>
                          <div className="text-muted-foreground">{a.city}, {a.state} — {a.pincode}</div>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </CardContent>
          </Card>

          {/* Delivery slot / ETA card — display-only, single express slot */}
          <Card className="border bg-gradient-to-r from-primary/[0.04] to-transparent" data-testid="checkout-slot-card">
            <CardContent className="p-4 md:p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Timer className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Delivery slot</div>
                <div className="text-sm md:text-base font-semibold leading-tight mt-0.5">Express · in ~{etaMinutes || 15} minutes</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Fastest available slot from your Babu Store</div>
              </div>
              <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-1 shrink-0">DEFAULT</span>
            </CardContent>
          </Card>

          {/* Payment block */}
          <Card className="border bg-card" data-testid="checkout-payment-card">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><CreditCard className="w-4 h-4" /></div>
                <div className="font-display text-base md:text-lg font-semibold">Payment method</div>
              </div>

              {/* Quick UPI launchers — mobile taps deep-link into the chosen app via
                  Razorpay's UPI intent flow. Desktop or missing apps fall back to the
                  standard Razorpay checkout automatically. */}
              <div className="mb-4" data-testid="quick-upi-row">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
                  <Smartphone className="w-3 h-3" /> Pay instantly with UPI
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { code: "google_pay", label: "Google Pay", short: "GPay", cls: "text-[#4285F4]" },
                    { code: "phonepe",    label: "PhonePe",    short: "PhonePe", cls: "text-[#5F259F]" },
                    { code: "paytm",      label: "Paytm",      short: "Paytm", cls: "text-[#00BAF2]" },
                    { code: "bhim",       label: "BHIM UPI",   short: "BHIM",  cls: "text-[#008B4B]" },
                  ].map((u) => (
                    <button
                      key={u.code}
                      onClick={() => place({ upiApp: u.code })}
                      disabled={placing || !selectedAddr || items.length === 0}
                      className="rounded-xl border-2 border-border hover:border-primary/60 hover:bg-primary/[0.03] transition p-2.5 flex flex-col items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed press"
                      title={`Pay with ${u.label}`}
                      data-testid={`quick-upi-${u.code}`}
                    >
                      <div className={`w-8 h-8 rounded-lg bg-secondary grid place-items-center font-bold text-[10px] tracking-tight ${u.cls}`}>
                        {u.short.slice(0, 4)}
                      </div>
                      <span className="text-[10px] font-semibold text-foreground/80 truncate max-w-full">{u.label}</span>
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1.5">Opens the app on your phone. Falls back to secure checkout if unavailable.</div>
              </div>

              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Or choose payment mode</div>
              <RadioGroup value={payment} onValueChange={setPayment} className="grid sm:grid-cols-2 gap-3">
                <label className={`rounded-xl border-2 p-4 cursor-pointer flex items-start gap-3 transition ${payment === "cod" ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/40"}`} data-testid="payment-cod">
                  <RadioGroupItem value="cod" className="mt-1" />
                  <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center shrink-0"><IndianRupee className="w-4 h-4" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">Cash on delivery</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">Pay in cash or UPI to the delivery partner.</div>
                  </div>
                </label>
                <label className={`rounded-xl border-2 p-4 cursor-pointer flex items-start gap-3 transition ${payment === "razorpay" ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/40"}`} data-testid="payment-razorpay">
                  <RadioGroupItem value="razorpay" className="mt-1" />
                  <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center shrink-0"><CreditCard className="w-4 h-4" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-1.5">Pay online <span className="text-[9px] font-bold uppercase text-primary bg-primary/10 rounded px-1 py-0.5">Instant</span></div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">UPI · Cards · Netbanking · Wallets. Razorpay secured.</div>
                  </div>
                </label>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Delivery notes */}
          <Card className="border bg-card" data-testid="checkout-notes-card">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><StickyNote className="w-4 h-4" /></div>
                <div className="font-display text-base md:text-lg font-semibold">Delivery instructions <span className="text-[11px] text-muted-foreground font-sans font-normal">(optional)</span></div>
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 300))}
                placeholder="e.g. Ring the bell twice · Leave at the door · Call on arrival"
                className="min-h-[72px] resize-none text-sm"
                data-testid="checkout-notes"
              />
              <div className="text-[10px] text-right text-muted-foreground mt-1">{notes.length}/300</div>
            </CardContent>
          </Card>
        </div>

        {/* Summary */}
        <div>
          <Card className="border bg-card sticky top-24" data-testid="checkout-summary">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Receipt className="w-4 h-4" /></div>
                <div className="font-display text-base md:text-lg font-semibold">Order summary</div>
                <span className="ml-auto text-[11px] text-muted-foreground">{items.length} item{items.length !== 1 ? "s" : ""}</span>
              </div>

              {/* Item list */}
              <div className="max-h-56 overflow-y-auto no-scrollbar divide-y -mx-1" data-testid="checkout-items">
                {items.map((it) => (
                  <div key={`${it.product_id}:${it.variant_id || ""}`} className="px-1 py-2 flex items-center gap-2 text-sm">
                    <div className="w-8 h-8 rounded-md bg-secondary text-foreground/50 grid place-items-center shrink-0">
                      {it.thumbnail_url
                        ? <img src={it.thumbnail_url} alt="" className="w-full h-full rounded-md object-cover" loading="lazy" />
                        : <Package className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{it.name}</div>
                      <div className="text-[11px] text-muted-foreground">Qty {it.qty}</div>
                    </div>
                    <div className="text-sm font-semibold tabular-nums shrink-0">{money(Number(it.price_snapshot) * Number(it.qty))}</div>
                  </div>
                ))}
              </div>

              {/* Savings banner */}
              {totalSavings > 0 && (
                <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 flex items-center gap-2 text-[12px] font-semibold" data-testid="checkout-savings">
                  <Sparkles className="w-3.5 h-3.5 shrink-0" />
                  You saved <span className="tabular-nums">{money(totalSavings)}</span> on this order
                </div>
              )}

              {/* Bill details */}
              <div className="pt-3 mt-3 border-t space-y-1.5 text-[13px]" data-testid="checkout-bill">
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Item total</span>
                  <span className="tabular-nums">{money(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />Coupon discount</span>
                    <span className="tabular-nums font-semibold" data-testid="checkout-discount">− {money(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" />GST &amp; taxes</span>
                  <span className="tabular-nums text-muted-foreground">Incl. {money(tax)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" />Delivery fee</span>
                  <span className="tabular-nums" data-testid="checkout-delivery">
                    {effectiveDelivery === 0
                      ? <span className="text-emerald-700 font-semibold">FREE</span>
                      : money(effectiveDelivery)}
                  </span>
                </div>
                {packaging > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Packaging</span>
                    <span className="tabular-nums">{money(packaging)}</span>
                  </div>
                )}
                {Number(freeOver) > 0 && effectiveDelivery > 0 && (
                  <div className="text-[11px] text-primary font-medium mt-1">
                    Add {money(Math.max(0, Number(freeOver) - subtotal))} more for FREE delivery
                  </div>
                )}
                <div className="flex justify-between text-lg md:text-xl font-display font-semibold pt-2 mt-2 border-t">
                  <span>Total</span>
                  <span className="tabular-nums" data-testid="checkout-total">{money(total)}</span>
                </div>
              </div>

              {/* Desktop Place order */}
              <Button
                onClick={place}
                size="lg"
                className="hidden md:flex w-full mt-5 btn-primary-glow h-12"
                disabled={placing || !selectedAddr || items.length === 0}
                data-testid="place-order-btn"
              >
                {placing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Placing…</> : <>Place order · {money(total)}</>}
              </Button>

              <div className="mt-4 space-y-1.5 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-primary" />Delivery in ~{etaMinutes || 15} minutes from Babu Store</div>
                <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-primary" />Secure order · GST invoice included</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" />100% freshness guaranteed</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky mobile Pay bar */}
      <div className="md:hidden fixed bottom-16 left-0 right-0 z-30 border-t bg-card/95 backdrop-blur px-3 py-2.5 flex items-center gap-3 slide-up-in" data-testid="checkout-mobile-bar">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total payable</div>
          <div className="text-lg font-bold tabular-nums leading-none" data-testid="checkout-mobile-total">{money(total)}</div>
        </div>
        <Button
          onClick={place}
          className="h-11 px-4 btn-primary-glow"
          disabled={placing || !selectedAddr || items.length === 0}
          data-testid="place-order-mobile-btn"
        >
          {placing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Placing…</> : <>Pay {payment === "cod" ? "on delivery" : "now"}</>}
        </Button>
      </div>

      {/* Full-screen placing overlay for premium loading state */}
      {placing && (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm grid place-items-center" data-testid="checkout-placing-overlay">
          <div className="bg-card rounded-2xl border shadow-xl px-6 py-5 flex items-center gap-3 slide-up-in">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <div>
              <div className="font-semibold">Placing your order…</div>
              <div className="text-xs text-muted-foreground">Reserving stock and confirming payment.</div>
            </div>
          </div>
        </div>
      )}

      {/* New address dialog */}
      <Dialog open={addrDialog} onOpenChange={setAddrDialog}>
        <DialogContent data-testid="address-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">New address</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label</Label><Input value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} placeholder="Home, Work…" /></div>
            <div><Label>Full name *</Label><Input value={addrForm.name} onChange={(e) => setAddrForm({ ...addrForm, name: e.target.value })} data-testid="addr-name" /></div>
            <div className="col-span-2"><Label>Phone *</Label><Input value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} placeholder="+91…" data-testid="addr-phone" /></div>
            <div className="col-span-2"><Label>Address line 1 *</Label><Input value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} placeholder="House / Flat / Street" data-testid="addr-line1" /></div>
            <div className="col-span-2"><Label>Landmark</Label><Input value={addrForm.landmark} onChange={(e) => setAddrForm({ ...addrForm, landmark: e.target.value })} placeholder="Near…" /></div>
            <div><Label>City *</Label><Input value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} data-testid="addr-city" /></div>
            <div><Label>State *</Label><Input value={addrForm.state} onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} data-testid="addr-state" /></div>
            <div><Label>Pincode *</Label><Input value={addrForm.pincode} onChange={(e) => setAddrForm({ ...addrForm, pincode: e.target.value })} data-testid="addr-pincode" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrDialog(false)}>Cancel</Button>
            <Button onClick={saveAddr} disabled={addrBusy || !addrForm.name || !addrForm.phone || !addrForm.line1 || !addrForm.city || !addrForm.state || !addrForm.pincode} className="btn-primary-glow" data-testid="save-addr-btn">
              {addrBusy ? "Saving…" : "Save address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
